// src/features/records/contexts/RecordDetailProvider.js
import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import RecordDetailContext from './RecordDetailContext';
import RecordDetail from '../components/RecordDetail';
import RecordDetailErrorBoundary from '../components/RecordDetail/RecordDetailErrorBoundary';
import RecordEditor from '../components/RecordEditor';
import ShareDialog from '../components/ShareDialog';
import ConfirmationModal from '../../../components/ConfirmationModal';
import { useDeleteRecord } from '../hooks/useRecordMutations';
import { useToast } from '../../../hooks/useToast';
import { sanitizeText } from '../../../utils/sanitize';
import mapRecordsErrorToKey from '../utils/recordsErrorMapper';

/**
 * Provider component for RecordDetail context.
 * Manages the state and rendering of RecordDetail modal across all view components.
 */
const RecordDetailProvider = ({ children }) => {
  const { t } = useTranslation();
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailMode, setDetailMode] = useState('modal');
  const [editorState, setEditorState] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [sharingRecord, setSharingRecord] = useState(null);
  const [openCommentsOnOpen, setOpenCommentsOnOpen] = useState(false);
  const editorKeyRef = useRef(0);
  const isEditorOpen = Boolean(editorState);
  const toast = useToast();
  const deleteRecordMutation = useDeleteRecord();

  // Open record detail with optional mode
  const openDetail = useCallback((record, options = {}) => {
    if (!record) {
      console.warn('openDetail called without a record');
      return;
    }
    
    setSelectedRecord(record);
    setOpenCommentsOnOpen(Boolean(options.openComments));
    if (options.mode) {
      setDetailMode(options.mode);
    }
  }, []);

  // Close record detail
  const closeDetail = useCallback(() => {
    setSelectedRecord(null);
    // Reset to default mode when closing
    setDetailMode('modal');
    setOpenCommentsOnOpen(false);
  }, []);

  // Handle Escape key for modal close (Requirement R5)
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && selectedRecord && detailMode === 'modal') {
        closeDetail();
      }
    };

    if (selectedRecord && detailMode === 'modal' && !isEditorOpen) {
      document.addEventListener('keydown', handleEscKey);
      return () => {
        document.removeEventListener('keydown', handleEscKey);
      };
    }
  }, [selectedRecord, detailMode, closeDetail, isEditorOpen]);

  const nextEditorKey = useCallback(() => {
    editorKeyRef.current += 1;
    return editorKeyRef.current;
  }, []);

  const openEditor = useCallback((record, initialLinks) => {
    setEditorState({
      key: nextEditorKey(),
      record: record ?? null,
      initialLinks
    });
  }, [nextEditorKey]);

  // Real edit handler that opens RecordEditor
  const editRecord = useCallback((record) => {
    // Don't close the detail view - set editing record
    openEditor(record);
  }, [openEditor]);

  // Real delete handler that shows confirmation
  const deleteRecord = useCallback((record) => {
    // Show confirmation modal instead of immediately deleting
    setDeleteConfirmation(record);
  }, []);

  // Real share handler that opens ShareDialog
  const shareRecord = useCallback((record) => {
    // Open the share dialog for the record
    setSharingRecord(record);
  }, []);

  // Handle save from RecordEditor
  const handleSaveRecord = useCallback(async () => {
    try {
      // Close the editor
      setEditorState(null);
    } catch (error) {
      console.error('Error in handleSaveRecord:', error);
      toast.error(t('records.detail.errors.save_failed', 'Failed to save record'));
    }
  }, [toast, t]);

  // Confirm delete action
  const confirmDelete = useCallback(async () => {
    const recordToDelete = deleteConfirmation;
    setDeleteConfirmation(null);

    if (!recordToDelete) return;

    // Close the RecordDetail modal if deleting the currently viewed record
    if (selectedRecord && selectedRecord.id === recordToDelete.id) {
      closeDetail();
    }

    // Use mutation hook for deleting
    deleteRecordMutation.mutate(recordToDelete.id, {
      onSuccess: () => {
        toast.success(t('records.detail.toasts.deleted_success', 'Record deleted successfully'));
      },
      onError: (error) => {
        console.error('Error deleting record:', error);

        const errorKey = mapRecordsErrorToKey(error, {
          fallbackKey: 'records.detail.errors.delete_failed'
        });
        toast.error(t(errorKey));
      }
    });
  }, [deleteConfirmation, selectedRecord, closeDetail, toast, deleteRecordMutation, t]);

  // Cancel delete action
  const cancelDelete = useCallback(() => {
    setDeleteConfirmation(null);
  }, []);

  // Handle link to record action
  const handleLinkToRecord = useCallback((recordId) => {
    openEditor(null, [
      {
        targetId: recordId,
        targetType: 'record',
        addedAt: new Date()
      }
    ]);
  }, [openEditor]);

  const contextValue = {
    selectedRecord,
    isOpen: !!selectedRecord,
    mode: detailMode,
    openDetail,
    closeDetail,
    editRecord,
    deleteRecord,
    shareRecord
  };

  const deleteTargetTitle = sanitizeText(deleteConfirmation?.content?.title || '')
    || t('records.detail.delete_confirm.fallback_title', 'this record');

  return (
    <RecordDetailContext.Provider value={contextValue}>
      {children}
      
      {/* Render RecordDetail with error boundary (Requirements R5, R7) */}
      {selectedRecord && (
        <RecordDetailErrorBoundary
          onError={(error, errorInfo) => {
            console.error('RecordDetail error:', error, errorInfo);
            // Could add error tracking/reporting here
          }}
        >
          <RecordDetail
            recordId={selectedRecord.id}
            mode={detailMode}
            initialData={selectedRecord}
            initialOpenComments={openCommentsOnOpen}
            onClose={closeDetail}
            onEdit={editRecord}
            onDelete={deleteRecord}
            onShare={shareRecord}
            onLinkToRecord={handleLinkToRecord}
            suspendModalInteractions={isEditorOpen}
            aria-modal="true"
          />
        </RecordDetailErrorBoundary>
      )}
      
      {/* Record Editor Modal */}
      {editorState && (
        <RecordEditor
          key={editorState.key}
          record={editorState.record}
          initialLinks={editorState.initialLinks}
          isOpen={!!editorState}
          onClose={() => setEditorState(null)}
          onSave={handleSaveRecord}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <ConfirmationModal
          isOpen={!!deleteConfirmation}
          onClose={cancelDelete}
          onConfirm={confirmDelete}
          title={t('records.detail.delete_confirm.title', 'Delete record')}
          message={t('records.detail.delete_confirm.message', {
            title: deleteTargetTitle,
            defaultValue: 'Are you sure you want to delete "{{title}}"? This action cannot be undone.'
          })}
          confirmText={t('records.actions.delete', 'Delete')}
          cancelText={t('common.cancel', 'Cancel')}
        />
      )}

      {/* Share Dialog */}
      {sharingRecord && (
        <ShareDialog
          record={sharingRecord}
          onClose={() => setSharingRecord(null)}
        />
      )}
    </RecordDetailContext.Provider>
  );
};

RecordDetailProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default RecordDetailProvider;
